import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useEffect, useMemo, useState } from "preact/hooks";
import { Button } from "../ui/button";
import { CalendarDays, CircleCheck, Loader2, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar } from "../ui/calendar";
import { OUTLINE_BRAND_BTN_CLASS, PRIMARY_BRAND_BTN_CLASS } from "./constants";
import { formatSubscriptionDateForDisplay } from "./formatters";
import { applySizeUpBundleStayAiSubscription } from "@/lib/api";
import { toastSubscriptionApiError } from "./subscription-errors";
import { trackBuyAgainWidgetInteract } from "@/lib/tracking";
import { BundleOffer } from "./BundleOffer";
import { buildBundleCardCopy } from "./cancel-chat-cards";
import type { SizeUpOffer } from "./size-up-eligibility";

/** Local YYYY-MM-DD for `date`. */
function toLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Billing-date window for accepting a size-up: tomorrow → six months out,
 * defaulting to the current next-billing date when still in the future.
 * Mirrors cancel-chat `bundleBillingDateWindow`.
 */
export function sizeUpBillingDateWindow(currentNextBillingDate: string | null): {
  min: string;
  max: string;
  initial: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const max = new Date(today);
  max.setMonth(max.getMonth() + 6);
  const min = toLocalYmd(tomorrow);
  const current = currentNextBillingDate?.slice(0, 10);
  const initial = current && current >= min ? current : min;
  return { min, max: toLocalYmd(max), initial };
}

export interface SizeUpReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offer: SizeUpOffer | null;
  trackingShop?: string | null;
  /** Called after a successful apply so the caller can suppress the banner. */
  onApplied?: () => void;
}

type Step = "review" | "success";

export function SizeUpReviewDialog({
  open,
  onOpenChange,
  offer,
  trackingShop,
  onApplied,
}: SizeUpReviewDialogProps) {
  const [step, setStep] = useState<Step>("review");
  const [chosenDate, setChosenDate] = useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  /** Draft date while the picker is open — committed only on Confirm. */
  const [draftDate, setDraftDate] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const win = useMemo(
    () => sizeUpBillingDateWindow(offer?.candidate.nextBillingDate ?? null),
    [offer?.candidate.nextBillingDate],
  );

  useEffect(() => {
    if (open) {
      setStep("review");
      setChosenDate(win.initial);
      setDatePickerOpen(false);
      setDraftDate(null);
    }
  }, [open, win.initial]);

  const resolvedDate = chosenDate ?? win.initial;
  const pickerDate = draftDate ?? resolvedDate;
  const bundle = offer?.bundle ?? null;
  const subscriptionId = offer?.candidate.subscriptionId;
  const acceptLabel = bundle
    ? buildBundleCardCopy(bundle).acceptLabel
    : "Switch to bigger size";

  const applyMutation = useMutation({
    mutationFn: () => {
      if (!bundle || subscriptionId == null) {
        throw new Error("Nothing to apply.");
      }
      return applySizeUpBundleStayAiSubscription(subscriptionId, {
        variantId: bundle.targetVariantId,
        price: bundle.targetNetPrice,
        unitPrice: bundle.targetUnitPrice,
        quantity: bundle.quantity,
        interval: bundle.newIntervalMonths,
        intervalUnit: "month",
        nextBillingDate: resolvedDate || undefined,
      });
    },
    onSuccess: () => {
      if (trackingShop && bundle && subscriptionId != null) {
        trackBuyAgainWidgetInteract("subscription_size_up_submit", trackingShop, {
          subscription_contract_id: subscriptionId,
          target_variant_id: bundle.targetVariantId,
          new_interval_months: bundle.newIntervalMonths,
          saving_per_month: bundle.savingPerMonth,
          next_billing_date: resolvedDate || undefined,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      onApplied?.();
      setStep("success");
    },
    onError: (error) => {
      if (trackingShop && bundle && subscriptionId != null) {
        trackBuyAgainWidgetInteract("subscription_size_up_failed", trackingShop, {
          subscription_contract_id: subscriptionId,
          target_variant_id: bundle.targetVariantId,
          new_interval_months: bundle.newIntervalMonths,
        });
      }
      toastSubscriptionApiError(error);
    },
  });

  const pending = applyMutation.isPending;
  const monthsLabel =
    bundle?.newIntervalMonths === 1
      ? "every month"
      : `every ${bundle?.newIntervalMonths ?? ""} months`;

  const openDatePicker = () => {
    if (pending) return;
    setDraftDate(resolvedDate);
    setDatePickerOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={onOpenChange}
          overlayClassName="tw:z-70"
          className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
        >
          {step === "success" ? (
            <SuccessBody onClose={() => onOpenChange(false)} />
          ) : (
            <>
              <DialogHeader>
                <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
                  <DialogTitle
                    className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    Size up &amp; save
                  </DialogTitle>
                  <DialogClose
                    data-slot="dialog-close"
                    render={
                      <Button
                        variant="ghost"
                        className="tw:p-0 tw:m-0 tw:shrink-0"
                        size="icon-sm"
                        aria-label="Close"
                      />
                    }
                  >
                    <X />
                  </DialogClose>
                </div>
              </DialogHeader>

              <div className="tw:mt-2 tw:overflow-y-auto tw:min-h-0 tw:flex tw:flex-col tw:gap-3">
                {bundle ? (
                  <BundleOffer bundle={bundle} deliveryPeriodSummary />
                ) : (
                  <p className="tw:text-sm tw:m-0 tw:text-[#523D33]/70">
                    This offer is no longer available.
                  </p>
                )}

                {bundle && (
                  <p
                    className="tw:text-sm tw:m-0"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    You&apos;ll get{" "}
                    <span className="tw:font-semibold">
                      {bundle.newServings} servings
                      {bundle.newSizeLabel ? ` (${bundle.newSizeLabel})` : ""}
                    </span>
                    , delivered {monthsLabel}.
                  </p>
                )}

                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <label
                    className="tw:text-sm tw:font-medium tw:text-[#523D33]"
                    htmlFor="size-up-next-delivery"
                  >
                    Next delivery
                  </label>
                  <button
                    id="size-up-next-delivery"
                    type="button"
                    disabled={pending}
                    onClick={openDatePicker}
                    className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-[#523D33]/20 tw:bg-white tw:px-3 tw:py-2.5 tw:text-left tw:text-sm tw:text-[#523D33] tw:cursor-pointer tw:transition-colors hover:tw:border-[#523D33]/40 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
                  >
                    <CalendarDays className="tw:size-4 tw:shrink-0 tw:text-[#523D33]/70" />
                    <span className="tw:flex-1 tw:font-medium">
                      {formatSubscriptionDateForDisplay(resolvedDate)}
                    </span>
                  </button>
                </div>
              </div>

              <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
                <Button
                  type="button"
                  disabled={pending || !bundle || !resolvedDate}
                  onClick={() => applyMutation.mutate()}
                  className={PRIMARY_BRAND_BTN_CLASS}
                >
                  {pending ? (
                    <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
                  ) : (
                    acceptLabel
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={() => onOpenChange(false)}
                  className={`${OUTLINE_BRAND_BTN_CLASS} tw:disabled:opacity-50`}
                >
                  Cancel
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <SizeUpBillingDateDialog
        open={datePickerOpen}
        value={pickerDate}
        min={win.min}
        max={win.max}
        onSelect={setDraftDate}
        onConfirm={(ymd) => {
          setChosenDate(ymd);
          setDatePickerOpen(false);
          setDraftDate(null);
        }}
        onCancel={() => {
          setDatePickerOpen(false);
          setDraftDate(null);
        }}
      />
    </>
  );
}

/** Nested date picker — same shape as cancel-chat `BundleBillingDateDialog`. */
function SizeUpBillingDateDialog({
  open,
  value,
  min,
  max,
  onSelect,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  value: string;
  min: string;
  max: string;
  onSelect: (ymd: string) => void;
  onConfirm: (ymd: string) => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName="tw:z-80"
        className="tw:z-81 tw:p-4 tw:max-w-sm tw:flex tw:flex-col tw:gap-3"
      >
        <DialogHeader className="tw:border-b tw:border-[#523D33]/15 tw:pb-3">
          <DialogTitle
            className="tw:text-left"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            Pick your next delivery date
          </DialogTitle>
        </DialogHeader>
        <div className="tw:rounded-2xl tw:border tw:border-[#523D33]/15 tw:bg-white tw:p-3">
          <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:text-[#523D33]">
            <CalendarDays className="tw:size-4 tw:shrink-0 tw:text-[#523D33]/70" />
            {formatSubscriptionDateForDisplay(value)}
          </div>
          <Calendar value={value} min={min} max={max} onSelect={onSelect} />
        </div>
        <DialogFooter className="tw:flex-col tw:gap-2">
          <Button
            type="button"
            className={PRIMARY_BRAND_BTN_CLASS}
            onClick={() => onConfirm(value)}
          >
            Confirm
          </Button>
          <Button
            type="button"
            variant="outline"
            className={OUTLINE_BRAND_BTN_CLASS}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuccessBody({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-4 tw:text-center tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-300">
        <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3 tw:mb-4">
          <CircleCheck
            className="tw:size-10 tw:text-[#523D33]"
            strokeWidth={1.5}
          />
        </div>
        <p
          className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-2"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          You&apos;re sized up!
        </p>
        <p className="tw:text-sm tw:m-0 tw:text-[#523D33]/80">
          Same servings each month, at a lower cost — your next delivery is
          locked in.
        </p>
      </div>
      <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-2 tw:pt-2">
        <Button type="button" onClick={onClose} className={PRIMARY_BRAND_BTN_CLASS}>
          OK
        </Button>
      </DialogFooter>
    </>
  );
}
